import { useCallback, useEffect, useMemo, useState } from "react";
import { apiFetch } from "@/lib/api-client";
import { useAuth } from "@/lib/auth-provider";
import { formatCurrency, formatDate, formatTime } from "@/lib/i18n";

interface PractitionerWorkspaceResponse {
  data: {
    workspaceDate: string;
    practitioner: {
      resourceId: string;
      name: string;
      staffMemberId: string | null;
      commissionConfig: {
        type: "flat" | "percentage" | "tiered";
        value?: number;
      } | null;
    } | null;
    bookings: Array<{
      id: string;
      serviceId: string;
      personId: string;
      startAt: string;
      endAt: string;
      status: "pending" | "confirmed" | "in_progress" | "completed" | "no_show";
      checkedInAt: string | null;
      customerNotes: string | null;
      internalNotes: string | null;
      commissionAmount: number;
      assignedResourceIds: string[];
      clientName: string;
      serviceName: string;
      durationMinutes: number;
      lastVisitAt: string | null;
      lastVisitServiceName: string | null;
    }>;
    summary: {
      scheduledCount: number;
      checkedInCount: number;
      completedCount: number;
      totalCommissionAmount: number;
      earnedCommissionAmount: number;
      projectedCommissionAmount: number;
    };
  };
}

function formatDuration(minutes: number) {
  if (minutes < 60) {
    return `${minutes}m`;
  }

  const hours = Math.floor(minutes / 60);
  const remaining = minutes % 60;
  return remaining === 0 ? `${hours}h` : `${hours}h ${remaining}m`;
}

function getTimerState(booking: PractitionerWorkspaceResponse["data"]["bookings"][number], now: Date) {
  const startAt = new Date(booking.startAt);
  const endAt = new Date(booking.endAt);

  if (booking.status === "completed") {
    return { tone: "neutral" as const, label: "Completed" };
  }

  if (booking.status === "no_show") {
    return { tone: "warning" as const, label: "Marked no-show" };
  }

  if (now < startAt) {
    const minutesUntilStart = Math.max(0, Math.round((startAt.getTime() - now.getTime()) / 60000));
    return { tone: "neutral" as const, label: `Starts in ${formatDuration(minutesUntilStart)}` };
  }

  const actualStart = booking.checkedInAt ? new Date(booking.checkedInAt) : startAt;
  const elapsedMinutes = Math.max(0, Math.round((now.getTime() - actualStart.getTime()) / 60000));

  if (now > endAt && booking.status === "in_progress") {
    const overtimeMinutes = Math.max(0, Math.round((now.getTime() - endAt.getTime()) / 60000));
    return { tone: "danger" as const, label: `Overtime ${formatDuration(overtimeMinutes)}` };
  }

  if (booking.status === "in_progress" || booking.checkedInAt) {
    return {
      tone: "success" as const,
      label: `Elapsed ${formatDuration(elapsedMinutes)} of ${formatDuration(booking.durationMinutes)}`,
    };
  }

  return { tone: "neutral" as const, label: "Ready for check-in" };
}

export default function PractitionerWorkspacePage() {
  const { user } = useAuth();
  const [workspace, setWorkspace] = useState<PractitionerWorkspaceResponse["data"] | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busyKey, setBusyKey] = useState<string | null>(null);
  const [notesDrafts, setNotesDrafts] = useState<Record<string, string>>({});
  const [now, setNow] = useState(() => new Date());

  const loadWorkspace = useCallback(async () => {
    setLoading(true);
    try {
      const response = await apiFetch<PractitionerWorkspaceResponse>("/crm/bookings/practitioner-workspace");
      setWorkspace(response.data);
      setNotesDrafts(Object.fromEntries(response.data.bookings.map((booking) => [booking.id, booking.internalNotes ?? ""])));
      setError(null);
    } catch (reason) {
      setWorkspace(null);
      setError(reason instanceof Error ? reason.message : "Failed to load practitioner workspace");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadWorkspace();
  }, [loadWorkspace]);

  useEffect(() => {
    const timer = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);

  const upcomingCount = useMemo(
    () => workspace?.bookings.filter((booking) => new Date(booking.startAt) > now).length ?? 0,
    [now, workspace?.bookings],
  );

  async function handleCheckIn(bookingId: string) {
    setBusyKey(`check-in:${bookingId}`);
    try {
      await apiFetch(`/crm/bookings/practitioner-workspace/${bookingId}/check-in`, {
        method: "POST",
      });
      await loadWorkspace();
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : "Failed to check in booking");
    } finally {
      setBusyKey(null);
    }
  }

  async function handleSaveNotes(bookingId: string) {
    setBusyKey(`notes:${bookingId}`);
    try {
      const nextNotes = notesDrafts[bookingId]?.trim() ?? "";
      await apiFetch(`/crm/bookings/practitioner-workspace/${bookingId}/notes`, {
        method: "PATCH",
        body: JSON.stringify({
          internalNotes: nextNotes.length > 0 ? nextNotes : null,
        }),
      });
      setWorkspace((current) => current ? {
        ...current,
        bookings: current.bookings.map((booking) => (
          booking.id === bookingId
            ? { ...booking, internalNotes: nextNotes.length > 0 ? nextNotes : null }
            : booking
        )),
      } : current);
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : "Failed to save notes");
    } finally {
      setBusyKey(null);
    }
  }

  if (loading) {
    return (
      <div className="rounded-3xl border border-border bg-background p-8 text-sm text-muted-foreground">
        Loading practitioner workspace...
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="rounded-3xl border border-border bg-background p-6 shadow-[0_24px_70px_-50px_rgba(15,23,42,0.45)]">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
          <div className="space-y-3">
            <p className="text-xs font-semibold uppercase tracking-[0.24em] text-[var(--color-primary)]">Mounted practitioner workspace</p>
            <h1 className="text-3xl font-semibold tracking-tight text-foreground">Today&apos;s service flow</h1>
            <p className="max-w-3xl text-sm leading-6 text-muted-foreground">
              This workspace is filtered to the logged-in practitioner’s assigned bookings on the live CRM seam. Use it for pre-briefing, check-in, session notes, timekeeping, and commission visibility without switching back to the operator calendar.
            </p>
          </div>
          <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
            <div className="rounded-2xl border border-border bg-muted/30 px-4 py-3">
              <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Practitioner</p>
              <p className="mt-2 text-sm font-semibold text-foreground">{workspace?.practitioner?.name ?? user?.name ?? "Unlinked"}</p>
            </div>
            <div className="rounded-2xl border border-border bg-muted/30 px-4 py-3">
              <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Scheduled today</p>
              <p className="mt-2 text-2xl font-semibold text-foreground">{workspace?.summary.scheduledCount ?? 0}</p>
            </div>
            <div className="rounded-2xl border border-border bg-muted/30 px-4 py-3">
              <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Upcoming</p>
              <p className="mt-2 text-2xl font-semibold text-foreground">{upcomingCount}</p>
            </div>
            <div className="rounded-2xl border border-border bg-muted/30 px-4 py-3">
              <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Workspace date</p>
              <p className="mt-2 text-sm font-semibold text-foreground">{workspace?.workspaceDate ?? "Today"}</p>
            </div>
          </div>
        </div>
      </div>

      {error ? (
        <div className="rounded-2xl border border-[var(--color-error)]/20 bg-[var(--color-error)]/8 p-4 text-sm text-[var(--color-error)]">
          {error}
        </div>
      ) : null}

      {!workspace?.practitioner ? (
        <div className="rounded-3xl border border-dashed border-border bg-background p-10 text-center">
          <h2 className="text-xl font-semibold text-foreground">No practitioner resource linked</h2>
          <p className="mt-2 text-sm text-muted-foreground">
            Link this user to a person resource in the Resources screen before using the practitioner workspace. The staff scheduling seam already lives there on the mounted booking resource runtime.
          </p>
        </div>
      ) : null}

      {workspace?.practitioner ? (
        <>
          <div className="grid gap-4 xl:grid-cols-3">
            <div className="rounded-3xl border border-border bg-background p-5">
              <p className="text-xs uppercase tracking-[0.22em] text-muted-foreground">Commission snapshot</p>
              <div className="mt-4 space-y-3">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-muted-foreground">Total scheduled</span>
                  <span className="font-semibold text-foreground">{formatCurrency(workspace.summary.totalCommissionAmount)}</span>
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-muted-foreground">Earned today</span>
                  <span className="font-semibold text-foreground">{formatCurrency(workspace.summary.earnedCommissionAmount)}</span>
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-muted-foreground">Still in play</span>
                  <span className="font-semibold text-foreground">{formatCurrency(workspace.summary.projectedCommissionAmount)}</span>
                </div>
              </div>
            </div>

            <div className="rounded-3xl border border-border bg-background p-5">
              <p className="text-xs uppercase tracking-[0.22em] text-muted-foreground">Attendance</p>
              <div className="mt-4 space-y-3">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-muted-foreground">Checked in</span>
                  <span className="font-semibold text-foreground">{workspace.summary.checkedInCount}</span>
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-muted-foreground">Completed</span>
                  <span className="font-semibold text-foreground">{workspace.summary.completedCount}</span>
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-muted-foreground">Open sessions</span>
                  <span className="font-semibold text-foreground">{Math.max(0, workspace.summary.scheduledCount - workspace.summary.completedCount)}</span>
                </div>
              </div>
            </div>

            <div className="rounded-3xl border border-border bg-background p-5">
              <p className="text-xs uppercase tracking-[0.22em] text-muted-foreground">Commission model</p>
              <p className="mt-4 text-sm text-foreground">
                {workspace.practitioner.commissionConfig
                  ? `${workspace.practitioner.commissionConfig.type}${workspace.practitioner.commissionConfig.value !== undefined ? ` • ${workspace.practitioner.commissionConfig.value}` : ""}`
                  : "No commission config saved on this practitioner resource."}
              </p>
            </div>
          </div>

          <div className="space-y-4">
            {workspace.bookings.length === 0 ? (
              <div className="rounded-3xl border border-dashed border-border bg-background p-10 text-center text-sm text-muted-foreground">
                No bookings assigned to this practitioner on {workspace.workspaceDate}.
              </div>
            ) : (
              workspace.bookings.map((booking) => {
                const timer = getTimerState(booking, now);
                const busyCheckIn = busyKey === `check-in:${booking.id}`;
                const busyNotes = busyKey === `notes:${booking.id}`;
                return (
                  <div key={booking.id} className="rounded-3xl border border-border bg-background p-5 shadow-[0_18px_50px_-45px_rgba(15,23,42,0.45)]">
                    <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
                      <div className="space-y-3">
                        <div className="flex flex-wrap items-center gap-3">
                          <h2 className="text-xl font-semibold text-foreground">{booking.clientName}</h2>
                          <span className="rounded-full bg-muted px-3 py-1 text-xs font-medium uppercase tracking-[0.16em] text-muted-foreground">
                            {booking.status.replace("_", " ")}
                          </span>
                          <span
                            className={[
                              "rounded-full px-3 py-1 text-xs font-medium uppercase tracking-[0.16em]",
                              timer.tone === "danger"
                                ? "bg-[var(--color-error)]/12 text-[var(--color-error)]"
                                : timer.tone === "success"
                                  ? "bg-[var(--color-success)]/12 text-[var(--color-success)]"
                                  : "bg-muted text-muted-foreground",
                            ].join(" ")}
                          >
                            {timer.label}
                          </span>
                        </div>
                        <p className="text-sm font-medium text-foreground">{booking.serviceName}</p>
                        <p className="text-sm text-muted-foreground">
                          {formatTime(booking.startAt)}
                          {" - "}
                          {formatTime(booking.endAt)}
                          {" · "}
                          {formatDuration(booking.durationMinutes)}
                        </p>
                      </div>

                      <div className="min-w-[220px] rounded-2xl border border-border bg-muted/30 px-4 py-3">
                        <p className="text-xs uppercase tracking-[0.18em] text-muted-foreground">Earnings</p>
                        <p className="mt-2 text-2xl font-semibold text-foreground">{formatCurrency(booking.commissionAmount)}</p>
                        <p className="mt-1 text-xs text-muted-foreground">
                          {booking.status === "completed" ? "Booked as earned commission" : "Projected from current commission data"}
                        </p>
                      </div>
                    </div>

                    <div className="mt-5 grid gap-4 xl:grid-cols-3">
                      <div className="rounded-2xl border border-border bg-muted/20 p-4">
                        <p className="text-xs uppercase tracking-[0.18em] text-muted-foreground">Pre-brief</p>
                        <div className="mt-3 space-y-2 text-sm">
                          <p className="text-foreground"><span className="font-medium">Service:</span> {booking.serviceName}</p>
                          <p className="text-foreground"><span className="font-medium">Client:</span> {booking.clientName}</p>
                          <p className="text-foreground">
                            <span className="font-medium">Last visit:</span>{" "}
                            {booking.lastVisitAt
                              ? `${formatDate(booking.lastVisitAt)}${booking.lastVisitServiceName ? ` · ${booking.lastVisitServiceName}` : ""}`
                              : "No completed visit on record"}
                          </p>
                          <p className="text-muted-foreground">
                            <span className="font-medium text-foreground">Client notes:</span>{" "}
                            {booking.customerNotes ?? "No customer notes provided."}
                          </p>
                        </div>
                      </div>

                      <div className="rounded-2xl border border-border bg-muted/20 p-4 xl:col-span-2">
                        <div className="flex items-center justify-between gap-3">
                          <p className="text-xs uppercase tracking-[0.18em] text-muted-foreground">Session notes</p>
                          <div className="flex flex-wrap gap-2">
                            <button
                              type="button"
                              onClick={() => void handleCheckIn(booking.id)}
                              disabled={busyCheckIn || !["pending", "confirmed"].includes(booking.status)}
                              className="rounded-lg border border-border px-3 py-2 text-sm font-medium text-foreground hover:bg-muted disabled:cursor-not-allowed disabled:opacity-60"
                            >
                              {busyCheckIn ? "Checking in..." : booking.checkedInAt || booking.status === "in_progress" || booking.status === "completed" ? "Checked in" : "Check in"}
                            </button>
                            <button
                              type="button"
                              onClick={() => void handleSaveNotes(booking.id)}
                              disabled={busyNotes}
                              className="rounded-lg bg-[var(--color-primary)] px-3 py-2 text-sm font-medium text-white hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-60"
                            >
                              {busyNotes ? "Saving..." : "Save notes"}
                            </button>
                          </div>
                        </div>

                        <textarea
                          value={notesDrafts[booking.id] ?? ""}
                          onChange={(event) => setNotesDrafts((current) => ({ ...current, [booking.id]: event.target.value }))}
                          placeholder="Capture prep details, treatment notes, or follow-up context for this session."
                          className="mt-3 min-h-32 w-full rounded-2xl border border-border bg-background px-4 py-3 text-sm text-foreground outline-none transition focus:border-[var(--color-primary)]"
                        />
                      </div>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </>
      ) : null}
    </div>
  );
}
